/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable camelcase */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { FormControlLabel } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";

import { useTranslation } from "react-i18next";
import { Field, reduxForm, change as changeFieldValue } from "redux-form";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Settings page components
import FormField from "components/FormField";
import { newAlertMessage } from "store/actions/globalAlertActions";

const FORM_NAME = "CustomProjectMenu";

function CustomProjectMenu({ handleSubmit, pristine, user, actNewAlertMessage, database }) {
  const { t } = useTranslation();

  const { customApp = {} } = user;
  const { menuConfig = {} } = customApp;

  const editDataAndSubmit = async (data) => {
    const updatedMenuConfig = {};

    Object.entries(menuConfig).forEach(([key, config]) => {
      const rawLabel = (data[`menu_label_${key}`] ?? "").trim();

      updatedMenuConfig[key] = {
        ...config,
        label: rawLabel === "" ? null : rawLabel,
        visible:
          typeof data[`menu_visible_${key}`] === "boolean"
            ? data[`menu_visible_${key}`]
            : config.visible ?? true,
      };
    });

    const dataToSend = {
      menuConfig: updatedMenuConfig,
    };

    try {
      await database.editCustomMenuConfigInMainUser(dataToSend);
      actNewAlertMessage({
        type: "success",
        title: t("Custom App"),
        content: `${t("configurationSent")}`,
      });
    } catch (error) {
      actNewAlertMessage({
        type: "error",
        title: t("Custom App"),
        content: `${t("Save")} ${t("failed")}`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(editDataAndSubmit)}>
      <Card id="basic-info" sx={{ overflow: "visible" }}>
        <MDBox p={3}>
          <MDTypography variant="h5">{t("Custom Project Menu")}</MDTypography>
        </MDBox>
        <MDBox pb={3} px={3}>
          <Grid container spacing={3}>
            {Object.entries(menuConfig).map(([key, cfg]) => (
              <React.Fragment key={key}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Field
                    name={`menu_label_${key}`}
                    component={FormField}
                    label={`${t("labelfor")}: ${t(cfg.defaultLabel)}`}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Field
                        name={`menu_visible_${key}`}
                        component={({ input }) => (
                          <Checkbox
                            checked={!!input.value}
                            onChange={(ev) => input.onChange(ev.target.checked)}
                          />
                        )}
                      />
                    }
                    label={t("visibleInMenu")}
                  />
                </Grid>
              </React.Fragment>
            ))}

            <Grid size={{ xs: 12 }}>
              <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  size="small"
                  disabled={pristine}
                >
                  {t("Save")}
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
    </form>
  );
}

CustomProjectMenu.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  database: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
  const { mainUserReducer } = state;

  const customApp = mainUserReducer?.customApp ?? {};
  const { menuConfig } = customApp;

  const initialValues = {};

  if (menuConfig) {
    Object.entries(menuConfig).forEach(([key, cfg]) => {
      initialValues[`menu_label_${key}`] = cfg.label ?? "";
      initialValues[`menu_visible_${key}`] = cfg.visible !== false;
    });
  }

  return { initialValues, user: mainUserReducer };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
  actChangeField: (form, field, value) => dispatch(changeFieldValue(form, field, value)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
  })(CustomProjectMenu)
);
